/**
 * O perfil do exame na tela (E151.F2, ADR-0103 §4.2): lê ao montar, a cada `releitura` (o caso
 * relido depois do exame) e a pedido.
 */
import { useCallback, useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import {
  lerPerfilDoExame,
  PerfilDoExameError,
  type MotivoDaFalhaDoPerfil,
  type PerfilDoExameData,
} from '@/lib/formalizacao';

export type EstadoDoPerfil =
  | { estado: 'carregando' }
  | { estado: 'erro'; motivo: MotivoDaFalhaDoPerfil }
  | { estado: 'pronto'; dados: PerfilDoExameData };

export function usePerfilDoExame(
  client: AxiosInstance,
  caseId: string,
  releitura: unknown,
): { perfil: EstadoDoPerfil; reler: () => void } {
  const [perfil, setPerfil] = useState<EstadoDoPerfil>({ estado: 'carregando' });
  const [pedido, setPedido] = useState(0);
  const reler = useCallback(() => setPedido((n) => n + 1), []);

  useEffect(() => {
    let vivo = true;
    setPerfil({ estado: 'carregando' });
    void (async () => {
      try {
        const dados = await lerPerfilDoExame(client, caseId);
        if (vivo) setPerfil({ estado: 'pronto', dados });
      } catch (err) {
        if (!vivo) return;
        const motivo = err instanceof PerfilDoExameError ? err.motivo : 'indisponivel';
        setPerfil({ estado: 'erro', motivo });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [client, caseId, releitura, pedido]);

  return { perfil, reler };
}
